/**
 * Where a new starter's case stands: five stages, each with a mark and words.
 *
 * ✅ done · 🔄 under way · ⏳ waiting on something outside the HelpDesk (the
 * hourly Entra sync) · ⬜ not yet · ➖ not needed. The mark is decoration; the
 * words carry the meaning, so a screen reader and a colour-blind reader get
 * the same thing. A raised request shows Ivanti's own status, verbatim.
 */

import { Timeline, type TimelineItem, Typography } from "@norican/react";
import { Link } from "react-router";

import { useT } from "@/i18n";
import type { StarterStage } from "@/lib/api";

import { stageMark, stageStatusKey, stageTimelineStatus } from "../lib/starters";

/** The words for a stage's status: ours before it is raised, Ivanti's after. */
function useStageWords(): (stage: StarterStage) => string {
  const { t } = useT("tickets");
  return (stage) => {
    const key = stageStatusKey(stage);
    if (key !== null) {
      return t(key as never);
    }
    return stage.provider_status ?? "";
  };
}

export interface StageTrackerProps {
  stages: StarterStage[];
  /** One line of chips for a card, rather than the full timeline. */
  compact?: boolean;
}

export function StageTracker({ stages, compact = false }: StageTrackerProps) {
  const { t } = useT("tickets");
  const words = useStageWords();

  if (compact) {
    return (
      <ul
        className="flex list-none flex-wrap gap-x-3 gap-y-1 p-0"
        aria-label={t("starters.tracker")}
      >
        {stages.map((stage) => (
          <li key={stage.key} className="flex items-center gap-1">
            <span aria-hidden>{stageMark(stage.state)}</span>
            <Typography as="span" variant="caption" className="text-fg">
              {t(`starters.stage.${stage.key}`)}
            </Typography>
            <Typography as="span" variant="caption" tone="muted">
              {words(stage)}
            </Typography>
          </li>
        ))}
      </ul>
    );
  }

  const items: TimelineItem[] = stages.map((stage) => ({
    id: stage.key,
    icon: <span aria-hidden>{stageMark(stage.state)}</span>,
    status: stageTimelineStatus(stage.state),
    tone: stage.state === "DONE" ? "success" : stage.state === "IN_PROGRESS" ? "accent" : "default",
    title: t(`starters.stage.${stage.key}`),
    timestamp: words(stage),
    description:
      (stage.requests ?? []).length === 0 ? undefined : (
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          {(stage.requests ?? []).map((request) => (
            <Link
              key={request.ticket_id}
              to={request.url_path}
              className="text-accent-fg-subtle text-sm font-medium underline-offset-2 hover:underline"
            >
              {t("starters.requestLink", {
                number: request.ticket_number,
                status: request.provider_status,
              })}
            </Link>
          ))}
        </span>
      ),
  }));
  return <Timeline items={items} aria-label={t("starters.tracker")} />;
}
